import { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../providers/AuthContext';
import { CategoryContext } from '../../providers/CategoryContext';
import { ProductContext } from '../../providers/ProductContext';
import { ProductTagContext } from '../../providers/ProductTagContext';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { showConfirm, showError, showSuccess } from '../../utils/sweetAlert';
import EnhancedMediaLibrary from './EnhancedMediaLibrary';
import { getCanonicalProductTag } from '../../config/productTags';

const COMMON_FIELDS = [
  ['category', 'Categoría'],
  ['subcategory', 'Subcategoría'],
  ['basePrice', 'Precio'],
  ['stock', 'Stock'],
  ['discount', 'Descuento'],
  ['description', 'Descripción'],
  ['brand', 'Marca'],
  ['material', 'Material'],
  ['tags', 'Tags'],
  ['dimensions', 'Dimensiones'],
  ['isActive', 'Estado'],
  ['variants', 'Variantes']
];

const getTemplateVariants = (variants = []) => variants.map(variant => ({
  size: variant.size || '',
  color: variant.color || '',
  price: variant.price ?? '',
  images: [],
  isActive: variant.isActive !== false
}));

const getDimensions = (dimensions = {}) => ({
  length: dimensions.length ?? '',
  width: dimensions.width ?? '',
  height: dimensions.height ?? ''
});

const getProductTemplateVariants = (product = {}) => {
  if (product.variants?.length) return getTemplateVariants(product.variants);

  const sizes = product.size || [];
  const colors = product.color || [];
  if (sizes.length === 0) return [];
  if (colors.length === 0) return sizes.map(size => ({ size, color: '', price: '', images: [], isActive: true }));

  return sizes.flatMap(size => colors.map(color => ({ size, color, price: '', images: [], isActive: true })));
};

const createProduct = (template = {}) => ({
  rowId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: '',
  description: template.description || '',
  category: template.category || '',
  subcategory: template.subcategory || '',
  basePrice: template.basePrice ?? '',
  stock: template.stock ?? '',
  discount: template.discount ?? 0,
  brand: template.brand || '',
  material: template.material || '',
  tags: template.tags || '',
  dimensions: getDimensions(template.dimensions),
  isActive: template.isActive !== false,
  variants: getTemplateVariants(template.variants),
  images: []
});

const formatTemplate = (product) => ({
  category: product.category?.name || product.category || '',
  subcategory: product.subcategory || '',
  basePrice: product.basePrice ?? product.price ?? '',
  stock: product.stock ?? '',
  discount: product.discount ?? 0,
  description: product.description || '',
  brand: product.brand || '',
  material: product.material || '',
  tags: (product.tags || []).join(', '),
  dimensions: getDimensions(product.dimensions),
  isActive: product.isActive !== false,
  variants: getProductTemplateVariants(product)
});

const BulkProductCreator = () => {
  const { token } = useContext(AuthContext);
  const { categories } = useContext(CategoryContext);
  const { products, refetchProducts } = useContext(ProductContext);
  const { productTags } = useContext(ProductTagContext);
  const [rows, setRows] = useState([createProduct()]);
  const [baseProductId, setBaseProductId] = useState('');
  const [baseSearch, setBaseSearch] = useState('');
  const [commonData, setCommonData] = useState(formatTemplate({}));
  const [selectedCommonFields, setSelectedCommonFields] = useState(new Set());
  const [mediaTarget, setMediaTarget] = useState(null);
  const [creating, setCreating] = useState(false);
  const [results, setResults] = useState(null);

  const availableBaseProducts = useMemo(() => {
    const normalizedSearch = baseSearch.trim().toLowerCase();
    return products
      .filter(product => !normalizedSearch || product.name.toLowerCase().includes(normalizedSearch))
      .slice(0, 30);
  }, [baseSearch, products]);

  const selectedBaseProduct = useMemo(
    () => products.find(product => product._id === baseProductId),
    [baseProductId, products]
  );

  useEffect(() => {
    if (!selectedBaseProduct && baseProductId) {
      fetch(`${process.env.REACT_APP_API_URL}/products/${baseProductId}`, {
        headers: getAdminAuthHeaders(token)
      })
        .then(response => response.ok ? response.json() : null)
        .then(product => product && setCommonData(formatTemplate(product)))
        .catch(() => {});
    }
  }, [baseProductId, selectedBaseProduct, token]);

  const getSubcategories = (categoryName) => (
    categories.find(category => category.name === categoryName)?.subcategories || []
  );

  const updateRow = (rowId, field, value) => {
    setRows(previousRows => previousRows.map(row => row.rowId === rowId ? { ...row, [field]: value } : row));
  };

  const updateVariant = (rowId, variantIndex, field, value) => {
    setRows(previousRows => previousRows.map(row => {
      if (row.rowId !== rowId) return row;
      return {
        ...row,
        variants: row.variants.map((variant, index) => index === variantIndex ? { ...variant, [field]: value } : variant)
      };
    }));
  };

  const handleBaseProductChange = (productId) => {
    setBaseProductId(productId);
    const product = products.find(item => item._id === productId);
    if (!product) return;

    const template = formatTemplate(product);
    setCommonData(template);
    setSelectedCommonFields(new Set(COMMON_FIELDS.map(([field]) => field)));
  };

  const applyCommonData = () => {
    if (selectedCommonFields.size === 0) {
      showError('Selecciona al menos un dato común para aplicar');
      return;
    }

    setRows(previousRows => previousRows.map(row => {
      const nextRow = { ...row };
      selectedCommonFields.forEach(field => {
        nextRow[field] = field === 'variants'
          ? getTemplateVariants(commonData.variants)
          : field === 'dimensions'
            ? getDimensions(commonData.dimensions)
            : commonData[field];
      });
      return nextRow;
    }));
    showSuccess(`Datos aplicados a ${rows.length} producto(s)`);
  };

  const addRow = () => {
    const template = selectedBaseProduct ? formatTemplate(selectedBaseProduct) : commonData;
    setRows(previousRows => [...previousRows, createProduct(template)]);
  };

  const duplicateRow = (row) => {
    setRows(previousRows => [...previousRows, createProduct(row)]);
  };

  const removeRow = (rowId) => {
    setRows(previousRows => previousRows.length === 1 ? previousRows : previousRows.filter(row => row.rowId !== rowId));
  };

  const addVariant = (rowId) => {
    setRows(previousRows => previousRows.map(row => row.rowId === rowId ? {
      ...row,
      variants: [...row.variants, { size: '', color: '', price: '', images: [], isActive: true }]
    } : row));
  };

  const removeVariant = (rowId, variantIndex) => {
    setRows(previousRows => previousRows.map(row => row.rowId === rowId ? {
      ...row,
      variants: row.variants.filter((_, index) => index !== variantIndex)
    } : row));
  };

  const openMediaLibrary = (rowId, target = 'main') => setMediaTarget({ rowId, target });

  const handleMediaSelect = (media) => {
    const imageRef = media.url || media.filename;
    if (!imageRef || !mediaTarget) return;

    setRows(previousRows => previousRows.map(row => {
      if (row.rowId !== mediaTarget.rowId) return row;
      if (mediaTarget.target === 'main') {
        return row.images.includes(imageRef) ? row : { ...row, images: [...row.images, imageRef] };
      }

      const variantIndex = mediaTarget.target.variantIndex;
      return {
        ...row,
        variants: row.variants.map((variant, index) => {
          if (index !== variantIndex || variant.images.includes(imageRef)) return variant;
          return { ...variant, images: [...variant.images, imageRef] };
        })
      };
    }));
  };

  const removeImage = (rowId, imageIndex, target = 'main') => {
    setRows(previousRows => previousRows.map(row => {
      if (row.rowId !== rowId) return row;
      if (target === 'main') return { ...row, images: row.images.filter((_, index) => index !== imageIndex) };
      return {
        ...row,
        variants: row.variants.map((variant, index) => index === target.variantIndex ? {
          ...variant,
          images: variant.images.filter((_, imagePosition) => imagePosition !== imageIndex)
        } : variant)
      };
    }));
  };

  const validateRows = () => {
    const errors = [];
    rows.forEach((row, index) => {
      const label = row.name.trim() || `Producto ${index + 1}`;
      if (!row.name.trim()) errors.push(`${label}: falta el nombre`);
      if (!row.description.trim()) errors.push(`${label}: falta la descripción`);
      if (!row.category) errors.push(`${label}: falta la categoría`);
      if (row.basePrice === '' || Number(row.basePrice) < 0 || Number.isNaN(Number(row.basePrice))) {
        errors.push(`${label}: el precio es inválido`);
      }
      if (row.stock !== '' && (!Number.isInteger(Number(row.stock)) || Number(row.stock) < 0)) {
        errors.push(`${label}: el stock es inválido`);
      }
      if (row.images.length === 0) errors.push(`${label}: agrega al menos una imagen`);
      row.variants.forEach((variant, variantIndex) => {
        if (!variant.size.trim()) errors.push(`${label}: la variante ${variantIndex + 1} no tiene talle`);
      });
    });
    return errors;
  };

  const buildPayload = () => rows.map(row => ({
    name: row.name.trim(),
    description: row.description.trim(),
    category: row.category,
    subcategory: row.subcategory,
    basePrice: Number(row.basePrice),
    stock: row.stock === '' ? 0 : Number(row.stock),
    discount: Number(row.discount) || 0,
    brand: row.brand.trim(),
    material: row.material.trim(),
    tags: [...new Set(row.tags.split(',').map(tag => getCanonicalProductTag(tag, productTags)).filter(Boolean))],
    dimensions: Object.fromEntries(Object.entries(row.dimensions).filter(([, value]) => value !== '').map(([key, value]) => [key, Number(value)])),
    isActive: row.isActive,
    images: row.images,
    variants: row.variants.map(variant => ({
      size: variant.size.trim(),
      color: variant.color.trim(),
      price: variant.price === '' ? undefined : Number(variant.price),
      images: variant.images,
      isActive: variant.isActive
    }))
  }));

  const handleCreate = async () => {
    const validationErrors = validateRows();
    if (validationErrors.length > 0) {
      showError(validationErrors.slice(0, 4).join('\n'));
      return;
    }

    const confirmation = await showConfirm(`Se crearán ${rows.length} productos. Revisa que las fotos y nombres sean correctos antes de continuar.`, 'Crear productos en lote');
    if (!confirmation.isConfirmed) return;

    try {
      setCreating(true);
      setResults(null);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/products/bulk-create`, {
        method: 'POST',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ products: buildPayload() })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo crear el lote');

      setResults(data.results);
      if (data.results.successful > 0) {
        const createdIndexes = new Set(data.results.created.map(product => product.index));
        setRows(previousRows => previousRows.filter((_, index) => !createdIndexes.has(index)));
        await refetchProducts();
        showSuccess(`${data.results.successful} producto(s) creado(s)`);
      }
    } catch (error) {
      showError(error.message || 'No se pudo crear el lote');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="bulkcreator-panel">
      <div className="bulkcreator-header">
        <div>
          <h1>Carga visual de productos</h1>
          <p>Prepara varias fichas con sus fotos, variantes y datos comunes antes de publicarlas.</p>
        </div>
        <button type="button" className="bulkcreator-primary-btn" onClick={addRow}>
          <i className="fas fa-plus"></i> Agregar producto
        </button>
      </div>

      <section className="bulkcreator-template-card">
        <div className="bulkcreator-section-heading">
          <div>
            <span className="bulkcreator-step">1</span>
            <h2>Producto base opcional</h2>
          </div>
          <p>Usa una ficha existente para copiar sus datos y variantes. Las imágenes siempre quedan vacías.</p>
        </div>
        <div className="bulkcreator-template-fields">
          <input value={baseSearch} onChange={(event) => setBaseSearch(event.target.value)} placeholder="Buscar producto base..." />
          <select value={baseProductId} onChange={(event) => handleBaseProductChange(event.target.value)}>
            <option value="">Crear desde cero</option>
            {availableBaseProducts.map(product => <option key={product._id} value={product._id}>{product.name}</option>)}
          </select>
        </div>
        {selectedBaseProduct && (
          <div className="bulkcreator-template-note">
            <i className="fas fa-copy"></i>
            <span>Se usarán datos y {selectedBaseProduct.variants?.length || 0} variante(s) de <strong>{selectedBaseProduct.name}</strong>. No se copiarán nombre, SKU, ID, slug ni imágenes.</span>
          </div>
        )}
      </section>

      <section className="bulkcreator-common-card">
        <div className="bulkcreator-section-heading">
          <div>
            <span className="bulkcreator-step">2</span>
            <h2>Datos comunes</h2>
          </div>
          <p>Marca qué campos quieres aplicar a todas las tarjetas. Luego puedes editar una ficha en particular.</p>
        </div>
        <div className="bulkcreator-common-toggle-list">
          {COMMON_FIELDS.map(([field, label]) => (
            <label key={field} className="bulkcreator-common-toggle">
              <input
                type="checkbox"
                checked={selectedCommonFields.has(field)}
                onChange={() => setSelectedCommonFields(previous => {
                  const next = new Set(previous);
                  next.has(field) ? next.delete(field) : next.add(field);
                  return next;
                })}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <div className="bulkcreator-common-grid">
          <label>Categoría
            <select value={commonData.category} onChange={(event) => setCommonData(previous => ({ ...previous, category: event.target.value, subcategory: '' }))}>
              <option value="">Seleccionar categoría</option>
              {categories.map(category => <option key={category._id} value={category.name}>{category.name}</option>)}
            </select>
          </label>
          <label>Subcategoría
            <select value={commonData.subcategory} disabled={!commonData.category} onChange={(event) => setCommonData(previous => ({ ...previous, subcategory: event.target.value }))}>
              <option value="">Sin subcategoría</option>
              {getSubcategories(commonData.category).map(subcategory => <option key={subcategory._id || subcategory.name} value={subcategory.name}>{subcategory.name}</option>)}
            </select>
          </label>
          <label>Precio base<input type="number" min="0" step="0.01" value={commonData.basePrice} onChange={(event) => setCommonData(previous => ({ ...previous, basePrice: event.target.value }))} /></label>
          <label>Stock<input type="number" min="0" step="1" value={commonData.stock} onChange={(event) => setCommonData(previous => ({ ...previous, stock: event.target.value }))} /></label>
          <label>Descuento (%)<input type="number" min="0" max="100" value={commonData.discount} onChange={(event) => setCommonData(previous => ({ ...previous, discount: event.target.value }))} /></label>
          <label>Marca<input value={commonData.brand} onChange={(event) => setCommonData(previous => ({ ...previous, brand: event.target.value }))} /></label>
          <label>Material<input value={commonData.material} onChange={(event) => setCommonData(previous => ({ ...previous, material: event.target.value }))} /></label>
          <label>Etiquetas<input value={commonData.tags} onChange={(event) => setCommonData(previous => ({ ...previous, tags: event.target.value }))} placeholder="Anime, Niños" /></label>
          <div className="bulkcreator-dimensions"><span>Dimensiones (cm)</span><input type="number" min="0" value={commonData.dimensions.length} placeholder="Largo" onChange={(event) => setCommonData(previous => ({ ...previous, dimensions: { ...previous.dimensions, length: event.target.value } }))} /><input type="number" min="0" value={commonData.dimensions.width} placeholder="Ancho" onChange={(event) => setCommonData(previous => ({ ...previous, dimensions: { ...previous.dimensions, width: event.target.value } }))} /><input type="number" min="0" value={commonData.dimensions.height} placeholder="Alto" onChange={(event) => setCommonData(previous => ({ ...previous, dimensions: { ...previous.dimensions, height: event.target.value } }))} /></div>
          <label className="bulkcreator-common-description">Descripción<textarea rows="3" value={commonData.description} onChange={(event) => setCommonData(previous => ({ ...previous, description: event.target.value }))} /></label>
          <label className="bulkcreator-active-toggle"><input type="checkbox" checked={commonData.isActive} onChange={(event) => setCommonData(previous => ({ ...previous, isActive: event.target.checked }))} /> Publicar activos</label>
        </div>
        <div className="bulkcreator-common-variants">
          <div><strong>Variantes comunes</strong><span>{commonData.variants.length} configurada(s)</span></div>
          <button type="button" onClick={() => setCommonData(previous => ({ ...previous, variants: [...previous.variants, { size: '', color: '', price: '', images: [], isActive: true }] }))}><i className="fas fa-plus"></i> Variante</button>
          {commonData.variants.map((variant, index) => (
            <div key={index} className="bulkcreator-variant-row">
              <input value={variant.size} placeholder="Talle" onChange={(event) => setCommonData(previous => ({ ...previous, variants: previous.variants.map((item, position) => position === index ? { ...item, size: event.target.value } : item) }))} />
              <input value={variant.color} placeholder="Color" onChange={(event) => setCommonData(previous => ({ ...previous, variants: previous.variants.map((item, position) => position === index ? { ...item, color: event.target.value } : item) }))} />
              <input type="number" min="0" step="0.01" value={variant.price} placeholder="Precio opcional" onChange={(event) => setCommonData(previous => ({ ...previous, variants: previous.variants.map((item, position) => position === index ? { ...item, price: event.target.value } : item) }))} />
              <button type="button" className="bulkcreator-icon-btn" onClick={() => setCommonData(previous => ({ ...previous, variants: previous.variants.filter((_, position) => position !== index) }))} aria-label="Quitar variante"><i className="fas fa-times"></i></button>
            </div>
          ))}
        </div>
        <button type="button" className="bulkcreator-apply-btn" onClick={applyCommonData}><i className="fas fa-wand-magic-sparkles"></i> Aplicar seleccionados a {rows.length} producto(s)</button>
      </section>

      <section className="bulkcreator-products-section">
        <div className="bulkcreator-section-heading">
          <div><span className="bulkcreator-step">3</span><h2>Productos del lote</h2></div>
          <p>Completa el nombre y asigna las fotos de cada diseño.</p>
        </div>
        <div className="bulkcreator-product-list">
          {rows.map((row, rowIndex) => (
            <article key={row.rowId} className="bulkcreator-product-card">
              <div className="bulkcreator-product-card-header">
                <span>Producto {rowIndex + 1}</span>
                <div>
                  <button type="button" className="bulkcreator-icon-btn" onClick={() => duplicateRow(row)} title="Duplicar datos sin imágenes"><i className="fas fa-copy"></i></button>
                  <button type="button" className="bulkcreator-icon-btn bulkcreator-danger-btn" onClick={() => removeRow(row.rowId)} disabled={rows.length === 1} title="Quitar producto"><i className="fas fa-trash"></i></button>
                </div>
              </div>
              <div className="bulkcreator-product-fields">
                <label>Nombre *<input value={row.name} onChange={(event) => updateRow(row.rowId, 'name', event.target.value)} placeholder="Ej: Canguro niños dinosaurio" /></label>
                <label>Precio *<input type="number" min="0" step="0.01" value={row.basePrice} onChange={(event) => updateRow(row.rowId, 'basePrice', event.target.value)} /></label>
                <label>Stock<input type="number" min="0" step="1" value={row.stock} onChange={(event) => updateRow(row.rowId, 'stock', event.target.value)} /></label>
                <label>Descuento (%)<input type="number" min="0" max="100" value={row.discount} onChange={(event) => updateRow(row.rowId, 'discount', event.target.value)} /></label>
                <label>Categoría *<select value={row.category} onChange={(event) => { updateRow(row.rowId, 'category', event.target.value); updateRow(row.rowId, 'subcategory', ''); }}><option value="">Seleccionar categoría</option>{categories.map(category => <option key={category._id} value={category.name}>{category.name}</option>)}</select></label>
                <label>Subcategoría<select value={row.subcategory} disabled={!row.category} onChange={(event) => updateRow(row.rowId, 'subcategory', event.target.value)}><option value="">Sin subcategoría</option>{getSubcategories(row.category).map(subcategory => <option key={subcategory._id || subcategory.name} value={subcategory.name}>{subcategory.name}</option>)}</select></label>
                <label>Marca<input value={row.brand} onChange={(event) => updateRow(row.rowId, 'brand', event.target.value)} /></label>
                <label>Material<input value={row.material} onChange={(event) => updateRow(row.rowId, 'material', event.target.value)} /></label>
                <label>Etiquetas<input value={row.tags} onChange={(event) => updateRow(row.rowId, 'tags', event.target.value)} placeholder="Anime, Niños" /></label>
                <div className="bulkcreator-dimensions"><span>Dimensiones (cm)</span><input type="number" min="0" value={row.dimensions.length} placeholder="Largo" onChange={(event) => updateRow(row.rowId, 'dimensions', { ...row.dimensions, length: event.target.value })} /><input type="number" min="0" value={row.dimensions.width} placeholder="Ancho" onChange={(event) => updateRow(row.rowId, 'dimensions', { ...row.dimensions, width: event.target.value })} /><input type="number" min="0" value={row.dimensions.height} placeholder="Alto" onChange={(event) => updateRow(row.rowId, 'dimensions', { ...row.dimensions, height: event.target.value })} /></div>
                <label className="bulkcreator-card-description">Descripción *<textarea rows="3" value={row.description} onChange={(event) => updateRow(row.rowId, 'description', event.target.value)} /></label>
              </div>
              <div className="bulkcreator-gallery-section">
                <div className="bulkcreator-gallery-heading"><div><strong>Galería *</strong><span>La primera foto será la portada</span></div><button type="button" onClick={() => openMediaLibrary(row.rowId)}><i className="fas fa-images"></i> Elegir fotos</button></div>
                <div className="bulkcreator-image-grid">
                  {row.images.map((image, imageIndex) => <div key={`${image}-${imageIndex}`} className="bulkcreator-image-item"><img src={getDisplayImageUrl(image)} alt={`Producto ${rowIndex + 1} ${imageIndex + 1}`} /><button type="button" onClick={() => removeImage(row.rowId, imageIndex)} aria-label="Quitar imagen"><i className="fas fa-times"></i></button>{imageIndex === 0 && <span>Portada</span>}</div>)}
                  {row.images.length === 0 && <button type="button" className="bulkcreator-add-image" onClick={() => openMediaLibrary(row.rowId)}><i className="fas fa-image"></i><span>Agregar fotos</span></button>}
                </div>
              </div>
              <div className="bulkcreator-card-variants">
                <div className="bulkcreator-gallery-heading"><div><strong>Variantes</strong><span>{row.variants.length} configurada(s)</span></div><button type="button" onClick={() => addVariant(row.rowId)}><i className="fas fa-plus"></i> Variante</button></div>
                {row.variants.map((variant, variantIndex) => <div key={variantIndex} className="bulkcreator-card-variant"><input value={variant.size} placeholder="Talle *" onChange={(event) => updateVariant(row.rowId, variantIndex, 'size', event.target.value)} /><input value={variant.color} placeholder="Color" onChange={(event) => updateVariant(row.rowId, variantIndex, 'color', event.target.value)} /><input type="number" min="0" step="0.01" value={variant.price} placeholder="Precio opcional" onChange={(event) => updateVariant(row.rowId, variantIndex, 'price', event.target.value)} /><button type="button" onClick={() => openMediaLibrary(row.rowId, { variantIndex })}><i className="fas fa-images"></i> {variant.images.length || ''} Fotos</button><button type="button" className="bulkcreator-icon-btn bulkcreator-danger-btn" onClick={() => removeVariant(row.rowId, variantIndex)} aria-label="Quitar variante"><i className="fas fa-times"></i></button></div>)}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bulkcreator-review-card">
        <div><strong>Listo para crear</strong><span>{rows.length} producto(s), {rows.reduce((total, row) => total + row.images.length, 0)} foto(s) y {rows.reduce((total, row) => total + row.variants.length, 0)} variante(s)</span></div>
        <button type="button" className="bulkcreator-primary-btn" onClick={handleCreate} disabled={creating}>{creating ? <><i className="fas fa-spinner fa-spin"></i> Creando...</> : <><i className="fas fa-rocket"></i> Crear productos</>}</button>
      </section>

      {results && <section className="bulkcreator-results-card"><h2>Resultado de la creación</h2><div className="bulkcreator-results-summary"><span><strong>{results.successful}</strong> creados</span><span><strong>{results.failed}</strong> con error</span></div>{results.created.length > 0 && <ul className="bulkcreator-created-list">{results.created.map(product => <li key={product._id}>{product.name}</li>)}</ul>}{results.errors.length > 0 && <div className="bulkcreator-error-list">{results.errors.map(error => <p key={`${error.index}-${error.error}`}><strong>{error.product}:</strong> {error.error}</p>)}</div>}</section>}

      {mediaTarget && <div className="bulkcreator-media-overlay" onClick={() => setMediaTarget(null)}><div className="bulkcreator-media-modal" onClick={(event) => event.stopPropagation()}><div className="bulkcreator-media-modal-header"><div><h2>Seleccionar imágenes</h2><p>Las fotos elegidas se agregarán a la ficha actual.</p></div><button type="button" className="bulkcreator-icon-btn" onClick={() => setMediaTarget(null)} aria-label="Cerrar"><i className="fas fa-times"></i></button></div><EnhancedMediaLibrary mode="selection" onSelect={handleMediaSelect} /><button type="button" className="bulkcreator-media-done" onClick={() => setMediaTarget(null)}>Listo</button></div></div>}
    </div>
  );
};

export default BulkProductCreator;
